import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
  useRef,
} from 'react';
import {
  MessageWithRelations,
  ConversationSummary,
  MessageType,
  TypingIndicatorPayload,
} from '../../../../apis/messaging/messaging.interfaces';
import {
  useGuardianGetAllMessagesHook,
  useGuardianGetDirectMessagesHook,
  useGuardianGetBroadcastMessagesHook,
  useGuardianGetConversationsHook,
  useGuardianSendDirectMessageHook,
  useGuardianSendBroadcastMessageHook,
  useGuardianMarkMessagesAsReadHook,
  useGuardianMessagingHook,
} from '../hooks/useGuardianMessagingHook';

export type ConversationFilter = 'all' | 'unread' | 'groups';

type TGuardianMessagingState = {
  // Messages
  allMessages: MessageWithRelations[];
  directMessages: MessageWithRelations[];
  broadcastMessages: MessageWithRelations[];

  // Conversations
  conversations: ConversationSummary[];

  // Current selection
  selectedReceiverId: string | null;
  selectedConversationId: string | null;
  selectedClassId: string | null;

  // Filters and search
  conversationFilter: ConversationFilter;
  searchQuery: string;

  // Typing indicators
  typingUsers: Map<string, TypingIndicatorPayload>;

  // Connection
  isConnected: boolean;
  connectionError: string | null;

  // Loading states
  isLoadingMessages: boolean;
  isLoadingConversations: boolean;
  isSendingMessage: boolean;

  // Error states
  error: string | null;
};

enum ACTION_TYPE {
  SET_ALL_MESSAGES = 'SET_ALL_MESSAGES',
  SET_DIRECT_MESSAGES = 'SET_DIRECT_MESSAGES',
  SET_BROADCAST_MESSAGES = 'SET_BROADCAST_MESSAGES',
  SET_CONVERSATIONS = 'SET_CONVERSATIONS',
  ADD_MESSAGE = 'ADD_MESSAGE',
  UPDATE_MESSAGE = 'UPDATE_MESSAGE',
  MARK_MESSAGES_READ = 'MARK_MESSAGES_READ',
  SET_SELECTED_RECEIVER = 'SET_SELECTED_RECEIVER',
  SET_SELECTED_CONVERSATION = 'SET_SELECTED_CONVERSATION',
  SET_SELECTED_CLASS = 'SET_SELECTED_CLASS',
  SET_CONVERSATION_FILTER = 'SET_CONVERSATION_FILTER',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_TYPING_USER = 'SET_TYPING_USER',
  REMOVE_TYPING_USER = 'REMOVE_TYPING_USER',
  SET_CONNECTION_STATUS = 'SET_CONNECTION_STATUS',
  SET_CONNECTION_ERROR = 'SET_CONNECTION_ERROR',
  SET_LOADING_MESSAGES = 'SET_LOADING_MESSAGES',
  SET_LOADING_CONVERSATIONS = 'SET_LOADING_CONVERSATIONS',
  SET_SENDING_MESSAGE = 'SET_SENDING_MESSAGE',
  SET_ERROR = 'SET_ERROR',
  RESET_STATE = 'RESET_STATE',
}

type Action =
  | { type: ACTION_TYPE.SET_ALL_MESSAGES; payload: MessageWithRelations[] }
  | { type: ACTION_TYPE.SET_DIRECT_MESSAGES; payload: MessageWithRelations[] }
  | {
    type: ACTION_TYPE.SET_BROADCAST_MESSAGES;
    payload: MessageWithRelations[];
  }
  | { type: ACTION_TYPE.SET_CONVERSATIONS; payload: ConversationSummary[] }
  | { type: ACTION_TYPE.ADD_MESSAGE; payload: MessageWithRelations }
  | {
    type: ACTION_TYPE.UPDATE_MESSAGE;
    payload: { messageId: string; updates: Partial<MessageWithRelations> };
  }
  | { type: ACTION_TYPE.MARK_MESSAGES_READ; payload: string[] }
  | { type: ACTION_TYPE.SET_SELECTED_RECEIVER; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_CONVERSATION; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_CLASS; payload: string | null }
  | { type: ACTION_TYPE.SET_CONVERSATION_FILTER; payload: ConversationFilter }
  | { type: ACTION_TYPE.SET_SEARCH_QUERY; payload: string }
  | { type: ACTION_TYPE.SET_TYPING_USER; payload: TypingIndicatorPayload }
  | { type: ACTION_TYPE.REMOVE_TYPING_USER; payload: string }
  | { type: ACTION_TYPE.SET_CONNECTION_STATUS; payload: boolean }
  | { type: ACTION_TYPE.SET_CONNECTION_ERROR; payload: string | null }
  | { type: ACTION_TYPE.SET_LOADING_MESSAGES; payload: boolean }
  | { type: ACTION_TYPE.SET_LOADING_CONVERSATIONS; payload: boolean }
  | { type: ACTION_TYPE.SET_SENDING_MESSAGE; payload: boolean }
  | { type: ACTION_TYPE.SET_ERROR; payload: string | null }
  | { type: ACTION_TYPE.RESET_STATE };

const initialState: TGuardianMessagingState = {
  allMessages: [],
  directMessages: [],
  broadcastMessages: [],
  conversations: [],
  selectedReceiverId: null,
  selectedConversationId: null,
  selectedClassId: null,
  conversationFilter: 'all',
  searchQuery: '',
  typingUsers: new Map(),
  isConnected: false,
  connectionError: null,
  isLoadingMessages: false,
  isLoadingConversations: false,
  isSendingMessage: false,
  error: null,
};

const guardianMessagingReducer = (
  state: TGuardianMessagingState,
  action: Action
): TGuardianMessagingState => {
  switch (action.type) {
    case ACTION_TYPE.SET_ALL_MESSAGES:
      return { ...state, allMessages: action.payload };

    case ACTION_TYPE.SET_DIRECT_MESSAGES:
      return { ...state, directMessages: action.payload };

    case ACTION_TYPE.SET_BROADCAST_MESSAGES:
      return { ...state, broadcastMessages: action.payload };

    case ACTION_TYPE.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };

    case ACTION_TYPE.ADD_MESSAGE:
      const newMessage = action.payload;
      const updatedAllMessages = [newMessage, ...state.allMessages];

      let updatedDirectMessages = state.directMessages;
      let updatedBroadcastMessages = state.broadcastMessages;

      if (newMessage.type === MessageType.DIRECT_MESSAGE) {
        updatedDirectMessages = [newMessage, ...updatedDirectMessages];
      } else if (newMessage.type === MessageType.BROADCAST) {
        updatedBroadcastMessages = [newMessage, ...updatedBroadcastMessages];
      }

      return {
        ...state,
        allMessages: updatedAllMessages,
        directMessages: updatedDirectMessages,
        broadcastMessages: updatedBroadcastMessages,
      };

    case ACTION_TYPE.UPDATE_MESSAGE:
      const { messageId, updates } = action.payload;
      const updateMessage = (msg: MessageWithRelations) =>
        msg._id === messageId ? { ...msg, ...updates } : msg;

      return {
        ...state,
        allMessages: state.allMessages.map(updateMessage),
        directMessages: state.directMessages.map(updateMessage),
        broadcastMessages: state.broadcastMessages.map(updateMessage),
      };

    case ACTION_TYPE.MARK_MESSAGES_READ:
      const messageIds = action.payload;
      const markAsRead = (msg: MessageWithRelations) =>
        messageIds.includes(msg._id || msg.id || '') ? { ...msg, is_read: true } : msg;

      return {
        ...state,
        allMessages: state.allMessages.map(markAsRead),
        directMessages: state.directMessages.map(markAsRead),
        broadcastMessages: state.broadcastMessages.map(markAsRead),
      };

    case ACTION_TYPE.SET_SELECTED_RECEIVER:
      return { ...state, selectedReceiverId: action.payload };

    case ACTION_TYPE.SET_SELECTED_CONVERSATION:
      return { ...state, selectedConversationId: action.payload };

    case ACTION_TYPE.SET_SELECTED_CLASS:
      return { ...state, selectedClassId: action.payload };

    case ACTION_TYPE.SET_CONVERSATION_FILTER:
      return { ...state, conversationFilter: action.payload };

    case ACTION_TYPE.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };

    case ACTION_TYPE.SET_TYPING_USER:
      const typingMap = new Map(state.typingUsers);
      typingMap.set(action.payload.conversationId, action.payload);
      return { ...state, typingUsers: typingMap };

    case ACTION_TYPE.REMOVE_TYPING_USER:
      const updatedTypingMap = new Map(state.typingUsers);
      updatedTypingMap.delete(action.payload);
      return { ...state, typingUsers: updatedTypingMap };

    case ACTION_TYPE.SET_CONNECTION_STATUS:
      return { ...state, isConnected: action.payload };

    case ACTION_TYPE.SET_CONNECTION_ERROR:
      return { ...state, connectionError: action.payload };

    case ACTION_TYPE.SET_LOADING_MESSAGES:
      return { ...state, isLoadingMessages: action.payload };

    case ACTION_TYPE.SET_LOADING_CONVERSATIONS:
      return { ...state, isLoadingConversations: action.payload };

    case ACTION_TYPE.SET_SENDING_MESSAGE:
      return { ...state, isSendingMessage: action.payload };

    case ACTION_TYPE.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTION_TYPE.RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

type TGuardianMessagingContextType = {
  state: TGuardianMessagingState;

  // Query hooks
  getAllMessages: ReturnType<typeof useGuardianGetAllMessagesHook>;
  getDirectMessages: ReturnType<typeof useGuardianGetDirectMessagesHook>;
  getBroadcastMessages: ReturnType<typeof useGuardianGetBroadcastMessagesHook>;
  getConversations: ReturnType<typeof useGuardianGetConversationsHook>;

  // Mutation hooks
  sendDirectMessage: ReturnType<typeof useGuardianSendDirectMessageHook>;
  sendBroadcastMessage: ReturnType<typeof useGuardianSendBroadcastMessageHook>;
  markMessagesAsRead: ReturnType<typeof useGuardianMarkMessagesAsReadHook>;

  // Service methods
  messagingService: ReturnType<typeof useGuardianMessagingHook>;

  // Actions
  setSelectedReceiver: (receiverId: string | null) => void;
  setSelectedConversation: (conversationId: string | null) => void;
  setSelectedClass: (classId: string | null) => void;
  setConversationFilter: (filter: ConversationFilter) => void;
  setSearchQuery: (query: string) => void;
  sendTypingIndicator: (payload: TypingIndicatorPayload) => void;
  sendStopTypingIndicator: (payload: TypingIndicatorPayload) => void;
  resetState: () => void;
};

const GuardianMessagingContext = createContext<TGuardianMessagingContextType | undefined>(
  undefined
);

export const GuardianMessagingProvider: FC<{ children: ReactNode; studentId: string }> = ({
  children,
  studentId,
}) => {
  const [state, dispatch] = useReducer(guardianMessagingReducer, initialState);

  const userParams = { page: 1, limit: 20 };

  // Initialize hooks with studentId
  const getAllMessagesQuery = useGuardianGetAllMessagesHook(studentId, userParams);
  const getDirectMessagesQuery = useGuardianGetDirectMessagesHook(
    studentId,
    state.selectedReceiverId || '',
    userParams,
    { enabled: !!state.selectedReceiverId } as Parameters<
      typeof useGuardianGetDirectMessagesHook
    >[3]
  );
  const getBroadcastMessagesQuery = useGuardianGetBroadcastMessagesHook(
    studentId,
    state.selectedClassId || undefined,
    userParams,
    { enabled: !!state.selectedClassId } as Parameters<
      typeof useGuardianGetBroadcastMessagesHook
    >[3]
  );
  const getConversationsQuery = useGuardianGetConversationsHook(studentId, userParams);

  const sendDirectMessageMutation = useGuardianSendDirectMessageHook(studentId);
  const sendBroadcastMessageMutation = useGuardianSendBroadcastMessageHook(studentId);
  const markMessagesAsReadMutation = useGuardianMarkMessagesAsReadHook(studentId);

  const messagingService = useGuardianMessagingHook(studentId);

  // Sync query results to state (same pattern as main messaging context)
  const prevLoadingRef = useRef<boolean>(false);
  const prevErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (getAllMessagesQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_ALL_MESSAGES,
        payload: getAllMessagesQuery.data.data,
      });
    }
    if (getAllMessagesQuery.isLoading !== prevLoadingRef.current) {
      prevLoadingRef.current = getAllMessagesQuery.isLoading;
      dispatch({
        type: ACTION_TYPE.SET_LOADING_MESSAGES,
        payload: getAllMessagesQuery.isLoading,
      });
    }
    if (getAllMessagesQuery.error) {
      const errorMessage =
        (getAllMessagesQuery.error as any)?.response?.data?.message ||
        (getAllMessagesQuery.error as Error)?.message ||
        'Failed to load messages';
      if (errorMessage !== prevErrorRef.current) {
        prevErrorRef.current = errorMessage;
        dispatch({
          type: ACTION_TYPE.SET_ERROR,
          payload: errorMessage,
        });
      }
    } else if (prevErrorRef.current !== null) {
      prevErrorRef.current = null;
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: null,
      });
    }
  }, [
    getAllMessagesQuery.data,
    getAllMessagesQuery.isLoading,
    getAllMessagesQuery.error,
  ]);

  useEffect(() => {
    if (getDirectMessagesQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_DIRECT_MESSAGES,
        payload: getDirectMessagesQuery.data.data,
      });
    }
  }, [getDirectMessagesQuery.data]);

  useEffect(() => {
    if (getBroadcastMessagesQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_BROADCAST_MESSAGES,
        payload: getBroadcastMessagesQuery.data.data,
      });
    }
  }, [getBroadcastMessagesQuery.data]);

  useEffect(() => {
    if (getConversationsQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_CONVERSATIONS,
        payload: getConversationsQuery.data.data,
      });
    }
    dispatch({
      type: ACTION_TYPE.SET_LOADING_CONVERSATIONS,
      payload: getConversationsQuery.isLoading,
    });
  }, [getConversationsQuery.data, getConversationsQuery.isLoading]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_SENDING_MESSAGE,
      payload:
        sendDirectMessageMutation.isPending ||
        sendBroadcastMessageMutation.isPending,
    });
  }, [
    sendDirectMessageMutation.isPending,
    sendBroadcastMessageMutation.isPending,
  ]);

  // Connection status
  const isConnectedFnRef = useRef(messagingService.isConnected);
  useEffect(() => {
    isConnectedFnRef.current = messagingService.isConnected;
  }, [messagingService.isConnected]);

  useEffect(() => {
    const checkConnection = () => {
      const connected = isConnectedFnRef.current();
      dispatch({ type: ACTION_TYPE.SET_CONNECTION_STATUS, payload: connected });
    };

    checkConnection();
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, []);

  // Real-time listeners
  const messagingServiceRef = useRef(messagingService);
  useEffect(() => {
    messagingServiceRef.current = messagingService;
  }, [messagingService]);

  useEffect(() => {
    const service = messagingServiceRef.current;
    const unsubscribeNewMessage = service.onNewMessage((message) => {
      dispatch({ type: ACTION_TYPE.ADD_MESSAGE, payload: message });
    });

    const unsubscribeMessageSent = service.onMessageSent((message) => {
      dispatch({ type: ACTION_TYPE.ADD_MESSAGE, payload: message });
    });

    const unsubscribeTyping = service.onTyping((indicator) => {
      dispatch({ type: ACTION_TYPE.SET_TYPING_USER, payload: indicator });

      // Auto-remove typing indicator after 3 seconds
      setTimeout(() => {
        dispatch({
          type: ACTION_TYPE.REMOVE_TYPING_USER,
          payload: indicator.conversationId,
        });
      }, 3000);
    });

    const unsubscribeStopTyping = service.onStopTyping((indicator) => {
      dispatch({
        type: ACTION_TYPE.REMOVE_TYPING_USER,
        payload: indicator.conversationId,
      });
    });

    const unsubscribeConnectionSuccess = service.onConnectionSuccess();
    // Connection success handled via onConnect already

    const unsubscribeError = service.onError((error) => {
      dispatch({
        type: ACTION_TYPE.SET_CONNECTION_ERROR,
        payload: error.message,
      });
      dispatch({ type: ACTION_TYPE.SET_ERROR, payload: error.message });
    });

    return () => {
      unsubscribeNewMessage();
      unsubscribeMessageSent();
      unsubscribeTyping();
      unsubscribeStopTyping();
      unsubscribeConnectionSuccess();
      unsubscribeError();
    };
  }, []);

  // Handle mutation success
  const getConversationsRefetchRef = useRef(getConversationsQuery.refetch);
  useEffect(() => {
    getConversationsRefetchRef.current = getConversationsQuery.refetch;
  }, [getConversationsQuery.refetch]);

  useEffect(() => {
    if (sendDirectMessageMutation.isSuccess && sendDirectMessageMutation.data) {
      // For guardian, sender_id should be studentId
      const messageWithCorrectSender: MessageWithRelations = {
        ...sendDirectMessageMutation.data,
        sender_id: studentId,
      };

      dispatch({
        type: ACTION_TYPE.ADD_MESSAGE,
        payload: messageWithCorrectSender,
      });
      // Refetch conversations to update last message
      getConversationsRefetchRef.current();
    }
  }, [sendDirectMessageMutation.isSuccess, sendDirectMessageMutation.data, studentId]);

  useEffect(() => {
    if (
      sendBroadcastMessageMutation.isSuccess &&
      sendBroadcastMessageMutation.data
    ) {
      // For guardian, sender_id should be studentId
      const messageWithCorrectSender: MessageWithRelations = {
        ...sendBroadcastMessageMutation.data,
        sender_id: studentId,
      };

      dispatch({
        type: ACTION_TYPE.ADD_MESSAGE,
        payload: messageWithCorrectSender,
      });
      // Refetch conversations to update last message
      getConversationsRefetchRef.current();
    }
  }, [
    sendBroadcastMessageMutation.isSuccess,
    sendBroadcastMessageMutation.data,
    studentId,
  ]);

  useEffect(() => {
    if (
      markMessagesAsReadMutation.isSuccess &&
      markMessagesAsReadMutation.data
    ) {
      // Messages are marked as read in the reducer when MARK_MESSAGES_READ action is dispatched
      // This effect can be used for additional side effects if needed
    }
  }, [markMessagesAsReadMutation.isSuccess, markMessagesAsReadMutation.data]);

  // Actions
  const setSelectedReceiver = useCallback((receiverId: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_RECEIVER, payload: receiverId });
  }, []);

  const setSelectedConversation = useCallback(
    (conversationId: string | null) => {
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_CONVERSATION,
        payload: conversationId,
      });
    },
    []
  );

  const setSelectedClass = useCallback((classId: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: classId });
  }, []);

  const setConversationFilter = useCallback((filter: ConversationFilter) => {
    dispatch({ type: ACTION_TYPE.SET_CONVERSATION_FILTER, payload: filter });
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: ACTION_TYPE.SET_SEARCH_QUERY, payload: query });
  }, []);

  const sendTypingIndicator = useCallback(
    (payload: TypingIndicatorPayload) => {
      messagingService.sendTypingIndicator(payload);
    },
    [messagingService]
  );

  const sendStopTypingIndicator = useCallback(
    (payload: TypingIndicatorPayload) => {
      messagingService.sendStopTypingIndicator(payload);
    },
    [messagingService]
  );

  const resetState = useCallback(() => {
    dispatch({ type: ACTION_TYPE.RESET_STATE });
  }, []);

  const contextValue = useMemo<TGuardianMessagingContextType>(
    () => ({
      state,
      getAllMessages: getAllMessagesQuery,
      getDirectMessages: getDirectMessagesQuery,
      getBroadcastMessages: getBroadcastMessagesQuery,
      getConversations: getConversationsQuery,
      sendDirectMessage: sendDirectMessageMutation,
      sendBroadcastMessage: sendBroadcastMessageMutation,
      markMessagesAsRead: markMessagesAsReadMutation,
      messagingService,
      setSelectedReceiver,
      setSelectedConversation,
      setSelectedClass,
      setConversationFilter,
      setSearchQuery,
      sendTypingIndicator,
      sendStopTypingIndicator,
      resetState,
    }),
    [
      state,
      getAllMessagesQuery,
      getDirectMessagesQuery,
      getBroadcastMessagesQuery,
      getConversationsQuery,
      sendDirectMessageMutation,
      sendBroadcastMessageMutation,
      markMessagesAsReadMutation,
      messagingService,
      setSelectedReceiver,
      setSelectedConversation,
      setSelectedClass,
      setConversationFilter,
      setSearchQuery,
      sendTypingIndicator,
      sendStopTypingIndicator,
      resetState,
    ]
  );

  return (
    <GuardianMessagingContext.Provider value={contextValue}>
      {children}
    </GuardianMessagingContext.Provider>
  );
};

export const useGuardianMessagingContext = () => {
  const context = useContext(GuardianMessagingContext);
  if (context === undefined) {
    throw new Error(
      'useGuardianMessagingContext must be used within a GuardianMessagingProvider'
    );
  }
  return context;
};
